import type { Ref } from 'react';
import type { FieldsetHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import { cx } from '../lib/cx';

export type FieldHintTone = 'success' | 'error' | 'warning' | 'info';

export interface FieldProps extends HTMLAttributes<HTMLDivElement> {
  label?: ReactNode;
  htmlFor?: string;

  required?: boolean;
  error?: ReactNode;
  errorId?: string;
  hint?: ReactNode;
  hintTone?: FieldHintTone;

  size?: 'default' | 'lg' | 'question';
  requiredLabel?: ReactNode;
  children: ReactNode;
}

export function Field(
  { ref, label,
    htmlFor,
    required = false,
    error,
    errorId,
    hint,
    hintTone,
    size = 'default',
    requiredLabel,
    className,
    children,
    ...rest
  }: FieldProps & { ref?: Ref<HTMLDivElement> },
) {
  return (
    <div ref={ref} className={cx(
        'field',
        size === 'lg' && 'field--lg',
        size === 'question' && 'field--question',
        className,
      )} {...rest}>
      {label !== undefined && (
        <label className="field-header" htmlFor={htmlFor}>
          {label}
          <RequiredMarker required={required} requiredLabel={requiredLabel} />
        </label>
      )}
      {children}
      <FieldError id={errorId}>{error}</FieldError>
      {hint && (
        <p className={cx('field-hint', hintTone && `is-${hintTone}`)}>
          {hint}
        </p>
      )}
    </div>
  );
}

export function FieldError({ id, children }: { id?: string; children?: ReactNode }) {
  if (!children) return null;
  return (
    <p className="field-error" id={id}>
      {children}
    </p>
  );
}

function RequiredMarker({ required, requiredLabel }: { required: boolean; requiredLabel?: ReactNode }) {
  if (!required) return null;
  if (requiredLabel != null) return <span className="field-required-label">{requiredLabel}</span>;
  return (
    <span className="field-required" aria-hidden="true">
      {' *'}
    </span>
  );
}

export interface FieldGroupProps extends FieldsetHTMLAttributes<HTMLFieldSetElement> {
  legend?: ReactNode;
  required?: boolean;
  requiredLabel?: ReactNode;
  error?: ReactNode;
  errorId?: string;
  hint?: ReactNode;
  hintTone?: FieldHintTone;
  size?: 'default' | 'lg' | 'question';
  children: ReactNode;
}

export function FieldGroup(
  { ref, legend, required = false, requiredLabel, error, errorId, hint, hintTone, size = 'default', className, children, ...rest }: FieldGroupProps & { ref?: Ref<HTMLFieldSetElement> },
) {
  return (
    <fieldset ref={ref} className={cx('field', size === 'lg' && 'field--lg', size === 'question' && 'field--question', className)} {...rest}>
      {legend != null && (
        <legend className="field-header">
          {legend}
          <RequiredMarker required={required} requiredLabel={requiredLabel} />
        </legend>
      )}
      {children}
      <FieldError id={errorId}>{error}</FieldError>
      {hint && (
        <p className={cx('field-hint', hintTone && `is-${hintTone}`)}>
          {hint}
        </p>
      )}
    </fieldset>
  );
}

export type FieldRowProps = HTMLAttributes<HTMLDivElement>;

export function FieldRow({ className, children, ...rest }: FieldRowProps) {
  return <div className={cx('field-row', className)} {...rest}>{children}</div>;
}
